<div class="team-dashboard">
  <!-- Main Content Grid -->
  <div class="row g-3 mb-4">
    <!-- Team Members -->
    <div class="col-12 col-lg-8">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h5 class="fw-semibold mb-0">Team Members</h5>
            <a href="#" class="text-primary text-decoration-none small">View All →</a>
          </div>

          <div class="team-members-list">
            <div v-if="team_members_loading" class="text-center py-5">
              <div class="spinner-border text-primary opacity-50" role="status"></div>
              <div class="text-muted small mt-2">Fetching team data...</div>
            </div>

            <div v-else-if="team_members.length > 0">
              <div v-for="member in team_members" :key="member.id" class="member-row d-flex align-items-center gap-3 p-3 mb-3 rounded-3 bg-light bg-opacity-50">
                <div class="member-avatar rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0" :style="{ backgroundColor: member.color}">
                  {{ member.initials }}
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="status-indicator rounded-circle" :class="member.status === 'online' ? 'bg-success' : 'bg-secondary'"></span>
                    <span class="fw-semibold">{{ member.name }}</span>
                  </div>
                  <div class="text-muted small">{{ member.role }} • {{ member.team }}</div>
                  <div class="progress mt-2 progress-slim">
                    <div class="progress-bar" :style="{ width: member.capacity + '%', background: 'linear-gradient(90deg, #4e73df, #224abe)' }"></div>
                  </div>
                </div>
                <div class="text-end flex-shrink-0">
                  <div class="d-flex gap-3 mb-2">
                    <div class="text-center">
                      <div class="fw-bold">{{ member.active }}</div>
                      <div class="text-muted fs-7">ACTIVE</div>
                    </div>
                    <div class="text-center">
                      <div class="fw-bold">{{ member.overdue }}</div>
                      <div class="text-muted fs-7">OVERDUE</div>
                    </div>
                    <div class="text-center">
                      <div class="fw-bold">{{ member.capacity }}%</div>
                      <div class="text-muted fs-7">CAPACITY</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-5 border rounded-4 border-dashed bg-light bg-opacity-50 h-100 d-flex flex-column align-items-center justify-content-center">
              <div class="mb-2">
                <i class="fa-solid fa-users-slash fa-2x text-muted opacity-50"></i>
              </div>
              <h6 class="fw-bold text-dark mb-1">No Team Members Found</h6>
              <p class="text-muted small mb-0 px-3">We couldn't find any members assigned to you.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Resource Allocation -->
    <div class="col-12 col-lg-4">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h5 class="fw-semibold mb-0">Resource Allocation by Product</h5>
            <a href="#" class="text-primary text-decoration-none small">Rebalance →</a>
          </div>

          <div class="resource-list">
            <div v-for="resource in resources" :key="resource.name" class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-medium">{{ resource.name }}</span>
                <span class="fw-bold">{{ resource.percentage }}%</span>
              </div>
              <div class="progress progress-medium">
                <div class="progress-bar" :style="{ width: resource.percentage + '%', backgroundColor: resource.color }"></div>
              </div>
            </div>

            <div class="mt-4 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted small">Total Team Hours This Week</span>
                <span class="fw-bold">{{ total_hours.used }} / {{ total_hours.total }} hrs</span>
              </div>
              <div class="progress mt-2 progress-medium">
                <div class="progress-bar" :style="{ width: (total_hours.used / total_hours.total * 100) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Second Row -->
  <div class="row g-3 mb-4">
    <!-- Critical Tasks -->
    <div class="col-12 col-lg-4">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h5 class="fw-semibold mb-0">Critical Tasks</h5>
            <span v-if="overdue_count > 0" class="badge bg-danger rounded-pill">
              {{ overdue_count }} OVERDUE
            </span>
          </div>

          <div class="tasks-list">
            <div v-if="tasks_loading" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-danger" role="status"></div>
              <div class="text-muted small mt-2">Checking priority tasks...</div>
            </div>

            <div v-else-if="critical_tasks.length > 0">
              <div v-for="task in critical_tasks" :key="task.id" class="task-item mb-3 p-3 rounded-3 bg-light">
                <div class="d-flex align-items-start gap-2 mb-2">
                  <span class="task-dot rounded-circle flex-shrink-0 mt-1" :style="{ backgroundColor: task.status_color }"></span>
                  <div class="flex-grow-1">
                    <div class="fw-semibold mb-1">{{ task.title }}</div>
                    <div class="text-muted small">Assigned: {{ task.assigned }} • Due: {{ task.due }}</div>
                  </div>
                </div>
                <span class="badge rounded-pill" :class="task.status_class">{{ task.status }}</span>
              </div>
            </div>

            <div v-else class="text-center py-5 border rounded-4 border-dashed bg-light bg-opacity-50">
              <div class="mb-2">
                <i class="fa-solid fa-circle-check fa-2x opacity-50"></i>
              </div>
              <h6 class="fw-bold text-dark mb-1">No Critical Tasks</h6>
              <p class="text-muted small mb-0">Your team is all caught up with high-priority items!</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sprint Velocity -->
    <div class="col-12 col-lg-4">
      <div class="card border-0 rounded-4 shadow-sm h-100">
        <div class="card-body p-4 d-flex flex-column">

          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="fw-bold mb-0">Active Workload</h5>
            <span v-if="velocity_data" class="badge rounded-pill bg-primary bg-opacity-10 text-primary px-3">
              {{ velocity_data.length }} Tasks
            </span>
          </div>

          <div class="flex-grow-1 custom-task-scroll px-1">

            <div v-if="velocity_loading" class="text-center py-5">
              <div class="spinner-border text-primary opacity-50" role="status"></div>
              <div class="text-muted small mt-2">Syncing with Engagenet...</div>
            </div>

            <div v-else-if="velocity_data && velocity_data.length > 0" class="vstack gap-2">
              <div v-for="(task, index) in velocity_data" :key="index"
                  class="p-3 rounded-3 border-start border-4 shadow-sm transition-hover"
                  :class="task.is_overdue ? 'border-danger bg-danger bg-opacity-10' : 'border-primary bg-light'">

                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="small fw-bold mb-0 text-truncate pe-2">{{ task.task_name }}</h6>
                  <span class="small text-nowrap" :class="task.is_overdue ? 'text-danger fw-bold' : 'text-muted'">
                    <i class="fa-regular fa-calendar me-1"></i>{{ task.due_date }}
                  </span>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2">
                    <div class="avatar-xs rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-bold">
                      {{ task.owner.charAt(0) }}
                    </div>
                    <span class="small text-secondary">{{ task.owner }}</span>
                  </div>
                  <span v-if="task.is_overdue" class="badge bg-danger text-uppercase tiny-font">Overdue</span>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-5 bg-light rounded-4 border border-dashed">
              <i class="fa-solid fa-circle-check fa-2x text-muted opacity-25 mb-2"></i>
              <p class="text-muted small mb-0">No tasks currently in progress.</p>
            </div>
          </div>

          <div class="mt-auto pt-3 border-top text-center">
            <p class="text-muted mb-0 extra-small">Live updates from IC Workbook</p>
          </div>

        </div>
      </div>
    </div>

    <!-- KPIs -->
    <div class="col-12 col-lg-4">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <h5 class="fw-semibold mb-4">Key Performance Metrics</h5>

          <div class="kpi-content-wrapper">
            <div v-if="kpis_loading" class="text-center py-5">
              <div class="spinner-border text-primary opacity-50" role="status"></div>
              <div class="text-muted small mt-2">Analyzing performance...</div>
            </div>

            <div v-else-if="kpis.length > 0">
              <div class="row g-3 mb-3">
                <div class="col-6" v-for="kpi in kpis" :key="kpi.label">
                  <div class="kpi-card p-3 rounded-3 border border-2" :style="{ borderColor: kpi.color }">
                    <div class="text-muted small mb-1">{{ kpi.label }}</div>
                    <div class="h4 fw-bold mb-0">{{ kpi.value }}</div>
                  </div>
                </div>
              </div>

              <div class="recognition-box p-3 rounded-3 mt-4 bg-success bg-opacity-25">
                <div class="fw-semibold mb-2 d-flex align-items-center gap-2">
                  <span>Team Message</span>
                </div>
                <div class="small">{{ recognition }}</div>
              </div>

              <div class="mt-4 text-center border-top pt-2">
                <span class="small text-muted">Metrics sourced from IC Workbook</span>
              </div>
            </div>

            <div v-else class="text-center py-5 border rounded-4 border-dashed bg-light bg-opacity-50 h-100 d-flex flex-column align-items-center justify-content-center">
              <div class="mb-2">
                <i class="fa-solid fa-chart-simple fa-2x text-muted opacity-50"></i>
              </div>
              <h6 class="fw-bold text-dark mb-1">No Performance Data</h6>
              <p class="text-muted small mb-0 px-3">Performance metrics will appear here once the team starts logging progress.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Third Row -->
  <div class="row g-3">
    <!-- Upcoming Deadlines -->
    <div class="col-12 col-lg-6">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <h5 class="fw-semibold mb-4">Upcoming Deadlines (Next 7 Days)</h5>

          <div class="deadlines-list">
            <div v-if="deadlines_loading" class="text-center py-5">
              <div class="spinner-border text-primary opacity-50" role="status"></div>
              <div class="text-muted small mt-2">Syncing deadlines...</div>
            </div>

            <div v-else-if="upcoming_deadlines.length > 0">
              <div v-for="deadline in upcoming_deadlines" :key="deadline.id" class="deadline-item p-3 mb-3 rounded-3 bg-light border-start border-3 border-info">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="flex-grow-1">
                    <div class="fw-semibold mb-1">{{ deadline.title }}</div>
                    <div class="text-muted small">{{ deadline.tasks_remaining }} tasks remaining</div>
                  </div>
                  <div class="text-end flex-shrink-0">
                    <div class="fw-bold" :style="{ color: deadline.urgency_color }">{{ deadline.date }}</div>
                    <div class="text-muted small">{{ deadline.days_left_text }}</div>
                  </div>
                </div>
              </div>
              <div class="mt-4 text-center border-top pt-2">
                <span class="small text-muted">Metrics sourced from IC Workbook</span>
              </div>
            </div>

            <div v-else class="text-center py-5 border rounded-4 border-dashed bg-light bg-opacity-50 h-100 d-flex flex-column align-items-center justify-content-center">
              <div class="mb-2">
                <i class="fa-solid fa-calendar-check fa-2x text-muted opacity-50"></i>
              </div>
              <h6 class="fw-bold text-dark mb-1">No Upcoming Deadlines</h6>
              <p class="text-muted small mb-0 px-3">All clear! No tasks due in the next 7 days.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Training -->
    <div class="col-12 col-lg-6">
      <div class="card border-0 rounded-4 shadow-lg h-100">
        <div class="card-body p-4">
          <h5 class="fw-semibold mb-4">Team Training Progress</h5>

          <div class="training-list">
            <div v-if="training_loading" class="text-center py-5">
              <div class="spinner-border text-info opacity-50" role="status"></div>
              <div class="text-muted small mt-2">Fetching learning progress...</div>
            </div>

            <div v-else-if="team_training.length > 0">
              <div v-for="training in team_training" :key="training.id" class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="fw-medium">{{ training.name }}</span>
                  <span class="fw-bold">{{ training.completed }}/{{ training.total }}</span>
                </div>
                <div class="progress progress-medium">
                  <div class="progress-bar" :style="{ width: (training.completed / training.total * 100) + '%', background: training.gradient }"></div>
                </div>
                <div class="text-muted small mt-1">{{ training.status }}</div>
              </div>

              <div class="mt-4 text-center border-top pt-2">
                <span class="small text-muted">Metrics sourced from IC Workbook</span>
              </div>
            </div>

            <div v-else class="text-center py-5 border rounded-4 border-dashed bg-light bg-opacity-50">
              <div class="mb-2">
                <i class="fa-solid fa-graduation-cap fa-2x text-muted opacity-50"></i>
              </div>
              <h6 class="fw-bold text-dark mb-1">No Active Training</h6>
              <p class="text-muted small mb-0">No learning initiatives are currently assigned.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>